<script lang="ts" setup>
import { computed, type ButtonHTMLAttributes } from "vue";
import { RouterLink, type RouteLocationRaw } from "vue-router";
import type { IconName } from "@/shared/@types/IconName";
import type { TextButtonColor } from "@/shared/@types/TextButtonColor";
import Icon from "../Icon/Icon.vue";
import LoadingDots from "../LoadingDots/LoadingDots.vue";

const props = withDefaults(
  defineProps<{
    caption: string;
    color?: TextButtonColor;
    size?: "s" | "m" | "l";
    type?: ButtonHTMLAttributes["type"];
    href?: string;
    target?: string;
    rel?: string;
    icon?: IconName;
    iconSize?: 20 | 24;
    iconPosition?: "leading" | "trailing" | "icon-only";
    display?: "block" | "inline";
    isLoading?: boolean;
    isDisabled?: boolean;
    to?: RouteLocationRaw;
  }>(),
  {
    color: "blue",
    size: "m",
    type: "button",
    iconSize: 20,
    iconPosition: "leading",
    display: "block",
    isLoading: false,
    isDisabled: false,
  },
);

const component = computed(() => {
  if (props.to) {
    return RouterLink;
  }
  if (props.href) {
    return "a";
  }
  return "button";
});

const conditionalAttributes = computed(() => {
  if (props.to) {
    return { to: props.to, target: props.target };
  }
  if (props.href) {
    return { href: props.href, target: props.target, rel: props.rel };
  }
  return { type: props.type };
});

const iconOnly = computed(() => props.iconPosition === "icon-only");
</script>

<template>
  <component
    :is="component"
    class="text-button"
    :disabled="isLoading || isDisabled"
    :data-color="color"
    :data-size="size"
    :data-icon-position="iconPosition"
    :data-display="display"
    v-bind="conditionalAttributes"
  >
    <span
      v-if="isLoading"
      class="text-button--loading"
    >
      <LoadingDots />
    </span>
    <Icon
      v-else-if="icon"
      :icon="icon"
      :size="iconSize"
    />
    <span
      class="text-button--caption"
      :class="{ 'screen-reader-only': iconOnly }"
    >
      {{ caption }}
    </span>
  </component>
</template>

<style scoped>
@import "TextButton.css";
</style>
